import React, { useState, useEffect } from 'react';
import { 
  FiEdit2, 
  FiTrash2, 
  FiCheck, 
  FiX, 
  FiCalendar, 
  FiUser,
  FiMapPin,
  FiFileText,
  FiAlertCircle,
  FiClock,
  FiRefreshCw
} from 'react-icons/fi';
import axiosInstance from '../../../libs/axios';
import toast from 'react-hot-toast';
import { FaRupeeSign } from 'react-icons/fa';

// Enums matching the backend
enum ClaimType {
  INHOUSE = 'inhouse',
  VENDOR = 'vendor',
}

enum ClaimStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PAID = 'paid',
}

enum TravelMode {
  SELF = 'self',
  CAB = 'cab',
}

interface ClaimData {
  id: number;
  user_id: number;
  project_site_id?: number;
  claim_type: ClaimType;
  activity_id?: number;
  claim_amount: number;
  advance_amount?: number;
  balance_amount?: number;
  description: string;
  status: ClaimStatus;
  submitted_at: string;
  approved_by?: number;
  approved_at?: string;
  paid_at?: string;
  rejection_reason?: string;
  taxi_proof_required?: boolean;
  travel_mode?: TravelMode;
  travel_slab_category?: string;
  slab_travel_amount?: number;
  extra_amount?: number;
  da_amount?: number;
  stay_amount?: number;
  cab_auto_amount?: number;
  incentive_amount?: number;
  stationary_amount?: number;
  misc_amount?: number;
  total_amount?: number;
  remarks?: string;
  attendance_site_distance?: number;
  reporting_site_distance?: number;
  final_distance?: number;
  created_at?: string;
  updated_at?: string;
}

interface ClaimManagementProps {
  siteId: number;
}

const ClaimTab: React.FC<ClaimManagementProps> = ({ siteId }) => {
  const [claim, setClaim] = useState<ClaimData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Form states
  const [editFormData, setEditFormData] = useState({
    claim_amount: 0,
    description: '',
    advance_amount: 0,
    travel_mode: TravelMode.SELF,
  });
  
  const [approveRemarks, setApproveRemarks] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [paymentRemarks, setPaymentRemarks] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchClaim();
  }, [siteId]);

  const fetchClaim = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(`/claims/${siteId}`);
      setClaim(response.data.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('No claim found for this site yet.');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch claim details');
      }
      setClaim(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClaim = async () => {
    if (!claim) return;
    
    try {
      setActionLoading(true);
      await axiosInstance.put(`/claims/${claim.id}`, editFormData);
      setShowEditModal(false);
      fetchClaim();
      toast.success('Claim updated successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update claim');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveClaim = async () => {
    if (!claim) return;
    
    try {
      setActionLoading(true);
      await axiosInstance.post(`/claims/${claim.id}/approve`, {
        remarks: approveRemarks || undefined,
      });
      setShowApproveModal(false);
      setApproveRemarks('');
      fetchClaim();
      toast.success('Claim approved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to approve claim');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClaim = async () => {
    if (!claim || !rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    
    try {
      setActionLoading(true);
      await axiosInstance.post(`/claims/${claim.id}/reject`, {
        rejection_reason: rejectionReason,
      });
      setShowRejectModal(false);
      setRejectionReason('');
      fetchClaim();
      toast.success('Claim rejected');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to reject claim');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!claim) return;
    
    try {
      setActionLoading(true);
      await axiosInstance.post(`/claims/${claim.id}/mark-paid`, {
        payment_remarks: paymentRemarks || undefined,
      });
      setShowPaymentModal(false);
      setPaymentRemarks('');
      fetchClaim();
      toast.success('Claim marked as paid');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to mark claim as paid');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClaim = async () => {
    if (!claim) return;
    
    try {
      setActionLoading(true);
      await axiosInstance.delete(`/claims/${claim.id}`);
      setShowDeleteModal(false);
      setClaim(null);
      toast.success('Claim deleted successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete claim');
    } finally {
      setActionLoading(false);
    }
  };

  const openEditModal = () => {
    if (claim) {
      setEditFormData({
        claim_amount: claim.claim_amount,
        description: claim.description,
        advance_amount: claim.advance_amount || 0,
        travel_mode: claim.travel_mode || TravelMode.SELF,
      });
      setShowEditModal(true);
    }
  };

  const getStatusColor = (status: ClaimStatus) => {
    switch (status) {
      case ClaimStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case ClaimStatus.APPROVED:
        return 'bg-green-100 text-green-800 border-green-200';
      case ClaimStatus.REJECTED:
        return 'bg-red-100 text-red-800 border-red-200';
      case ClaimStatus.PAID:
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '₹0.00';
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <FiRefreshCw className="w-10 h-10 text-blue-800 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-600">Loading claim details...</p>
        </div>
      </div>
    );
  }

  if (error && !claim) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white border border-gray-200 rounded shadow-sm p-6 max-w-md w-full text-center">
          <FiAlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-800 mb-2">No Claim Found</h3>
          <p className="text-sm text-gray-600">{error}</p>
          <button
            onClick={fetchClaim}
            className="mt-4 px-4 py-2 bg-blue-800 hover:bg-blue-900 text-white text-sm rounded transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!claim) return null;

  const canEdit = true
  const canApprove = true
  const canReject = true
  const canMarkPaid = true
  const canDelete = true

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white border border-gray-200 rounded shadow-sm p-4 md:p-6 mb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h1 className="text-lg font-bold text-gray-900 mb-1">Claim Details</h1>
              <p className="text-xs text-gray-500">Claim ID: #{claim.id}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded text-xs font-medium border ${getStatusColor(claim.status)}`}>
                {claim.status?.toUpperCase()}
              </span>
              <span className="px-3 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                {claim.claim_type?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-4">
            {/* Amount Card */}
            <div className="bg-white border border-gray-200 rounded shadow-sm p-4">
              <h2 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <FaRupeeSign className="w-4 h-4" />
                Financial Details
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-blue-50 border border-blue-100 rounded p-3">
                  <p className="text-xs text-gray-600 mb-1">Claim Amount</p>
                  <p className="text-base font-bold text-blue-800">{formatCurrency(claim.claim_amount)}</p>
                </div>
                {claim.advance_amount !== undefined && claim.advance_amount > 0 && (
                  <div className="bg-amber-50 border border-amber-100 rounded p-3">
                    <p className="text-xs text-gray-600 mb-1">Advance</p>
                    <p className="text-base font-bold text-amber-800">{formatCurrency(claim.advance_amount)}</p>
                  </div>
                )}
                {claim.balance_amount !== undefined && claim.balance_amount > 0 && (
                  <div className="bg-green-50 border border-green-100 rounded p-3">
                    <p className="text-xs text-gray-600 mb-1">Balance</p>
                    <p className="text-base font-bold text-green-800">{formatCurrency(claim.balance_amount)}</p>
                  </div>
                )}
                {claim.total_amount !== undefined && claim.total_amount > 0 && (
                  <div className="bg-purple-50 border border-purple-100 rounded p-3">
                    <p className="text-xs text-gray-600 mb-1">Total Amount</p>
                    <p className="text-base font-bold text-purple-800">{formatCurrency(claim.total_amount)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Description Card */}
            <div className="bg-white border border-gray-200 rounded shadow-sm p-4">
              <h2 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <FiFileText className="w-4 h-4" />
                Description
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed">{claim.description}</p>
              {claim.remarks && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-600 mb-1">Remarks:</p>
                  <p className="text-sm text-gray-700">{claim.remarks}</p>
                </div>
              )}
            </div>

            {/* Travel Details */}
            {claim.travel_mode && (
              <div className="bg-white border border-gray-200 rounded shadow-sm p-4">
                <h2 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <FiMapPin className="w-4 h-4" />
                  Travel Details
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <div>
                    <p className="text-gray-600 mb-1">Travel Mode</p>
                    <p className="font-medium text-gray-800 capitalize">{claim.travel_mode}</p>
                  </div>
                  {claim.slab_travel_amount !== undefined && claim.slab_travel_amount > 0 && (
                    <div>
                      <p className="text-gray-600 mb-1">Slab Amount</p>
                      <p className="font-medium text-gray-800">{formatCurrency(claim.slab_travel_amount)}</p>
                    </div>
                  )}
                  {claim.da_amount !== undefined && claim.da_amount > 0 && (
                    <div>
                      <p className="text-gray-600 mb-1">DA Amount</p>
                      <p className="font-medium text-gray-800">{formatCurrency(claim.da_amount)}</p>
                    </div>
                  )}
                  {claim.stay_amount !== undefined && claim.stay_amount > 0 && (
                    <div>
                      <p className="text-gray-600 mb-1">Stay Amount</p>
                      <p className="font-medium text-gray-800">{formatCurrency(claim.stay_amount)}</p>
                    </div>
                  )}
                  {claim.cab_auto_amount !== undefined && claim.cab_auto_amount > 0 && (
                    <div>
                      <p className="text-gray-600 mb-1">Cab/Auto</p>
                      <p className="font-medium text-gray-800">{formatCurrency(claim.cab_auto_amount)}</p>
                    </div>
                  )}
                  {claim.final_distance !== undefined && claim.final_distance > 0 && (
                    <div>
                      <p className="text-gray-600 mb-1">Final Distance</p>
                      <p className="font-medium text-gray-800">{claim.final_distance} km</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Additional Amounts */}
            {(claim.extra_amount || claim.incentive_amount || claim.stationary_amount || claim.misc_amount) && (
              <div className="bg-white border border-gray-200 rounded shadow-sm p-4">
                <h2 className="text-sm font-semibold text-gray-800 mb-3">Additional Amounts</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  {claim.extra_amount !== undefined && claim.extra_amount > 0 && (
                    <div>
                      <p className="text-gray-600 mb-1">Extra</p>
                      <p className="font-medium text-gray-800">{formatCurrency(claim.extra_amount)}</p>
                    </div>
                  )}
                  {claim.incentive_amount !== undefined && claim.incentive_amount > 0 && (
                    <div>
                      <p className="text-gray-600 mb-1">Incentive</p>
                      <p className="font-medium text-gray-800">{formatCurrency(claim.incentive_amount)}</p>
                    </div>
                  )}
                  {claim.stationary_amount !== undefined && claim.stationary_amount > 0 && (
                    <div>
                      <p className="text-gray-600 mb-1">Stationary</p>
                      <p className="font-medium text-gray-800">{formatCurrency(claim.stationary_amount)}</p>
                    </div>
                  )}
                  {claim.misc_amount !== undefined && claim.misc_amount > 0 && (
                    <div>
                      <p className="text-gray-600 mb-1">Miscellaneous</p>
                      <p className="font-medium text-gray-800">{formatCurrency(claim.misc_amount)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Rejection Reason */}
            {claim.rejection_reason && (
              <div className="bg-red-50 border border-red-200 rounded p-4">
                <h2 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
                  <FiX className="w-4 h-4" />
                  Rejection Reason
                </h2>
                <p className="text-sm text-red-700">{claim.rejection_reason}</p>
              </div>
            )}
          </div>

          {/* Right Column - Timeline & Actions */}
          <div className="space-y-4">
            {/* Timeline Card */}
            <div className="bg-white border border-gray-200 rounded shadow-sm p-4">
              <h2 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <FiClock className="w-4 h-4" />
                Timeline
              </h2>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <FiCalendar className="w-3 h-3 text-gray-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-700">Submitted</p>
                    <p className="text-xs text-gray-600">{formatDate(claim.submitted_at)}</p>
                  </div>
                </div>
                {claim.approved_at && (
                  <div className="flex items-start gap-2">
                    <FiCheck className="w-3 h-3 text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-gray-700">Approved</p>
                      <p className="text-xs text-gray-600">{formatDate(claim.approved_at)}</p>
                    </div>
                  </div>
                )}
                {claim.paid_at && (
                  <div className="flex items-start gap-2">
                    <FaRupeeSign className="w-3 h-3 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-gray-700">Paid</p>
                      <p className="text-xs text-gray-600">{formatDate(claim.paid_at)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Info Card */}
            <div className="bg-white border border-gray-200 rounded shadow-sm p-4">
              <h2 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <FiUser className="w-4 h-4" />
                Information
              </h2>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">User ID</span>
                  <span className="font-medium text-gray-800">#{claim.user_id}</span>
                </div>
                {claim.project_site_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Project Site</span>
                    <span className="font-medium text-gray-800">#{claim.project_site_id}</span>
                  </div>
                )}
                {claim.activity_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Activity ID</span>
                    <span className="font-medium text-gray-800">#{claim.activity_id}</span>
                  </div>
                )}
                {claim.approved_by && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Approved By</span>
                    <span className="font-medium text-gray-800">#{claim.approved_by}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions Card */}
            <div className="bg-white border border-gray-200 rounded shadow-sm p-4">
              <h2 className="text-sm font-semibold text-gray-800 mb-3">Actions</h2>
              <div className="space-y-2">
                {/* {canEdit && (
                  <button
                    onClick={openEditModal}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-800 hover:bg-blue-900 text-white text-sm rounded transition-colors"
                  >
                    <FiEdit2 className="w-3 h-3" />
                    Edit Claim
                  </button>
                )} */}
                
                {canApprove && (
                  <button
                    onClick={() => setShowApproveModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                  >
                    <FiCheck className="w-3 h-3" />
                    Approve
                  </button>
                )}
                
                {canReject && (
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                  >
                    <FiX className="w-3 h-3" />
                    Reject
                  </button>
                )}
                
                {canMarkPaid && (
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                  >
                    <FaRupeeSign className="w-3 h-3" />
                    Mark as Paid
                  </button>
                )}
                
                {/* {canDelete && (
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                  >
                    <FiTrash2 className="w-3 h-3" />
                    Delete
                  </button>
                )} */}

                <button
                  onClick={fetchClaim}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded transition-colors border border-gray-300"
                >
                  <FiRefreshCw className="w-3 h-3" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Edit Claim</h3>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Claim Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.claim_amount}
                  onChange={(e) => setEditFormData({ ...editFormData, claim_amount: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Advance Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.advance_amount}
                  onChange={(e) => setEditFormData({ ...editFormData, advance_amount: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Travel Mode
                </label>
                <select
                  value={editFormData.travel_mode}
                  onChange={(e) => setEditFormData({ ...editFormData, travel_mode: e.target.value as TravelMode })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent"
                >
                  <option value={TravelMode.SELF}>Self</option>
                  <option value={TravelMode.CAB}>Cab</option>
                </select>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex gap-2 justify-end">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateClaim}
                disabled={actionLoading}
                className="px-4 py-2 text-sm bg-blue-800 hover:bg-blue-900 text-white rounded transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg max-w-md w-full">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Approve Claim</h3>
            </div>
            <div className="p-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Remarks (Optional)
              </label>
              <textarea
                value={approveRemarks}
                onChange={(e) => setApproveRemarks(e.target.value)}
                rows={3}
                placeholder="Add any remarks..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent"
              />
            </div>
            <div className="p-4 border-t border-gray-200 flex gap-2 justify-end">
              <button
                onClick={() => setShowApproveModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleApproveClaim}
                disabled={actionLoading}
                className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg max-w-md w-full">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Reject Claim</h3>
            </div>
            <div className="p-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Rejection Reason *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                placeholder="Provide a reason for rejection (10-500 characters)"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">{rejectionReason.length}/500 characters</p>
            </div>
            <div className="p-4 border-t border-gray-200 flex gap-2 justify-end">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectClaim}
                disabled={actionLoading || rejectionReason.length < 10}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg max-w-md w-full">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Mark Claim as Paid</h3>
            </div>
            <div className="p-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Payment Remarks (Optional)
              </label>
              <textarea
                value={paymentRemarks}
                onChange={(e) => setPaymentRemarks(e.target.value)}
                rows={3}
                placeholder="Add payment details or transaction ID..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent"
              />
            </div>
            <div className="p-4 border-t border-gray-200 flex gap-2 justify-end">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleMarkAsPaid}
                disabled={actionLoading}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Mark as Paid'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg max-w-md w-full">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Delete Claim</h3>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-700">
                Are you sure you want to delete this claim? This action cannot be undone.
              </p>
            </div>
            <div className="p-4 border-t border-gray-200 flex gap-2 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteClaim}
                disabled={actionLoading}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimTab;